import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera as CameraIcon, DoorOpen, Bell, Monitor } from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';

// ── Stat Summary Row ──────────────────────────────────────────
// The 2x2 quad of tappable stat cards. Shared by the desktop dashboard (Home)
// and MobileHome. Self-contained: fetches its own data, owns its routes.
export default function StatSummaryRow({ tenantReady, tenantVersion }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    cameras: null, events: null, alerts: null, devices: null,
  });

  useEffect(() => {
    if (!tenantReady) return;
    let cancelled = false;
    const fetchStats = async () => {
      const safe = async (fn) => { try { return await fn(); } catch { return null; } };

      const [camerasOnline, eventsToday, activeAlerts, devicesMonitored] = await Promise.all([
        safe(async () => {
          const r = await fetchWithTenant('/api/vms/cameras');
          if (!r.ok) return null;
          const d = await r.json();
          const list = Array.isArray(d) ? d : (d.cameras || []);
          return list.filter(c => String(c.status || '').toLowerCase() === 'online').length;
        }),
        safe(async () => {
          const r = await fetchWithTenant('/api/acs/events');
          if (!r.ok) return null;
          const d = await r.json();
          const list = Array.isArray(d) ? d : (d.events || []);
          const today = new Date(); today.setHours(0, 0, 0, 0);
          return list.filter(e => {
            const t = new Date(e.timestamp || e.eventTime || e.createdAt || 0);
            return !isNaN(t) && t >= today;
          }).length;
        }),
        safe(async () => {
          const r = await fetchWithTenant('/api/md/alerts?status=active');
          if (!r.ok) return null;
          const d = await r.json();
          const list = Array.isArray(d) ? d : (d.alerts || []);
          return list.length;
        }),
        safe(async () => {
          const r = await fetchWithTenant('/api/md/devices');
          if (!r.ok) return null;
          const d = await r.json();
          const list = Array.isArray(d) ? d : (d.devices || []);
          return list.length;
        }),
      ]);

      if (cancelled) return;
      setStats({
        cameras: camerasOnline,
        events: eventsToday,
        alerts: activeAlerts,
        devices: devicesMonitored,
      });
    };
    fetchStats();
    return () => { cancelled = true; };
  }, [tenantReady, tenantVersion]);

  const cards = [
    { key: 'cameras', label: 'Cameras Online', value: stats.cameras, icon: CameraIcon, to: '/cameras' },
    { key: 'events', label: 'Access Events Today', value: stats.events, icon: DoorOpen, to: '/access?tab=live' },
    { key: 'alerts', label: 'Active Alerts', value: stats.alerts, icon: Bell, to: '/monitoring' },
    { key: 'devices', label: 'Devices Monitored', value: stats.devices, icon: Monitor, to: '/monitoring/devices' },
  ];

  return (
    <div className="dashboard-stat-row">
      {cards.map(c => (
        <div key={c.key} className="dashboard-stat-card" onClick={() => navigate(c.to)}>
          <div className="dashboard-stat-icon"><c.icon size={20} /></div>
          <div className="dashboard-stat-body">
            <div className="dashboard-stat-value">{c.value === null ? '--' : c.value}</div>
            <div className="dashboard-stat-label">{c.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
